import type { PrimitiveAtom } from 'jotai'
import { useSetAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import type { Callback } from '../atom-with-listeners'
import { LifecycleState, PersistenceState } from './use-lifecycle-persistence'

// generic sync utility
function useSyncToAtom<T>(atomRef: PrimitiveAtom<T>, value: T, opts?: { store?: any }) {
	const setAtom = useSetAtom(atomRef, opts)
	useEffect(() => {
		setAtom(value)
	}, [value, setAtom])
}

// New: wire lifecycle directly to an existing data atom + its listener (no proxy bundle)
// TAtom can be T | undefined (for atoms that support undefined) or just T (for atoms with default values)
export function useSyncLifecycleToExistingAtom<T, TAtom = T | undefined>(
	lifecycleOutput: {
		data: T | undefined
		setData: (v: T) => void
		lifecycle: LifecycleState
		persistence: PersistenceState
		isLocked: boolean
	},
	atoms: {
		dataAtom: PrimitiveAtom<TAtom>
		useDataListener: (cb: Callback<TAtom>, opts?: { store?: any }) => void
		lifecycleAtom?: PrimitiveAtom<LifecycleState>
		persistenceAtom?: PrimitiveAtom<PersistenceState>
		isLockedAtom?: PrimitiveAtom<boolean>
	},
	options?: { store?: any },
) {
	const { data, setData, lifecycle, persistence, isLocked } = lifecycleOutput
	const { dataAtom, useDataListener, lifecycleAtom: lcAtom, persistenceAtom: psAtom, isLockedAtom: lkAtom } = atoms

	// Prevent feedback: when we push data into the atom, ignore the listener for that tick
	const isInternalSyncRef = useRef(false)
	const setExternalDataAtom = useSetAtom(dataAtom, options)

	useEffect(() => {
		isInternalSyncRef.current = true
		setExternalDataAtom(data as TAtom)
		// allow listener after microtask
		queueMicrotask(() => {
			isInternalSyncRef.current = false
		})
	}, [data, setExternalDataAtom])

	// listen to external writes and route them through setData
	useDataListener((_get, _set, newVal) => {
		if (isInternalSyncRef.current) { return }
		if (newVal != undefined) {
			setData(newVal as T)
		}
	}, options)

	// wire lifecycle flags if provided
	if (lcAtom) { useSyncToAtom(lcAtom, lifecycle, options) }
	if (psAtom) { useSyncToAtom(psAtom, persistence, options) }
	if (lkAtom) { useSyncToAtom(lkAtom, isLocked, options) }
}
